import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import type { WinLossSlice } from "./chart-data";
import { CHART_FONT, CSS_VARS, useChartTheme } from "./chart-theme";

interface Props {
	data: WinLossSlice[];
}

const CustomTooltip = ({
	active,
	payload,
}: {
	active?: boolean;
	payload?: Array<{ payload: WinLossSlice }>;
}) => {
	if (!active || !payload?.[0]) return null;
	const p = payload[0].payload;
	return (
		<div
			className="rounded-md border px-2.5 py-1.5 text-[0.72rem] shadow-lg"
			style={{
				background: CSS_VARS.tooltip,
				borderColor: CSS_VARS.tooltipBorder,
			}}
		>
			<p style={{ color: p.color }} className="font-semibold">
				{p.name}: {p.value}
			</p>
		</div>
	);
};

const RADIAN = Math.PI / 180;

export const WinLossBreakdownChart = ({ data }: Props) => {
	const theme = useChartTheme();

	if (data.length === 0) {
		return (
			<p className="flex h-full items-center justify-center text-[0.78rem] tg-text-muted">
				No closed trades to show.
			</p>
		);
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const renderLabel = (props: any) => {
		const cx = Number(props.cx ?? 0);
		const cy = Number(props.cy ?? 0);
		const midAngle = Number(props.midAngle ?? 0);
		const innerRadius = Number(props.innerRadius ?? 0);
		const outerRadius = Number(props.outerRadius ?? 0);
		const name = String(props.name ?? "");
		const percent = Number(props.percent ?? 0);

		const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
		const x = cx + radius * Math.cos(-midAngle * RADIAN);
		const y = cy + radius * Math.sin(-midAngle * RADIAN);
		if (percent < 0.08) return null;
		return (
			<text
				x={x}
				y={y}
				fill={theme.pieLabelFill}
				textAnchor="middle"
				dominantBaseline="central"
				fontSize={CHART_FONT.size}
				fontWeight={600}
			>
				{name} {(percent * 100).toFixed(0)}%
			</text>
		);
	};

	return (
		<ResponsiveContainer width="100%" height="100%">
			<PieChart>
				<Pie
					data={data}
					dataKey="value"
					nameKey="name"
					cx="50%"
					cy="50%"
					outerRadius="80%"
					label={renderLabel}
					labelLine={false}
					strokeWidth={0}
					isAnimationActive={false}
				>
					{data.map((entry) => (
						<Cell key={entry.name} fill={entry.color} />
					))}
				</Pie>
				<Tooltip content={<CustomTooltip />} />
			</PieChart>
		</ResponsiveContainer>
	);
};
